// navbar.component.ts (completo y corregido: colapsa por desbordamiento y vuelve a modo normal al agrandar)
import {
  Component,
  inject,
  Input,
  OnInit,
  ViewChild,
  ElementRef,
  HostListener,
  OnDestroy,
  ViewChildren,
  QueryList,
  ChangeDetectorRef,
  EventEmitter,
  Output,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { ButtonLinkComponent } from '../components/button-link/button-link.component';
import { ButtonMenuComponent } from '../components/button-menu/button-menu.component';
import { DropdownInterface, Navbar } from '../interfaces/cpe-interfaces.index';
import { DropdownMenuComponent } from '../components/dropdown-menu/dropdown-menu.component';
import { ButtonComponent } from '../components/button/button.component';
import { ImagesUtil } from '@cpe/app/utils/image.util';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    ButtonLinkComponent,
    ButtonMenuComponent,
    DropdownMenuComponent,
    ButtonComponent,
    RouterModule,
  ],
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  providers: [ImagesUtil],
  host: { '[class.is-mobile]': 'isMobile' },
})
export class NavbarComponent implements OnInit, OnDestroy {
  @Input() appName!: string;
  @Input() navbar!: Navbar;
  @Output()
  submenuSelected = new EventEmitter<{
    title: string;
    items: { name: string; url: string }[];
  }>();

  readonly imageUtils = inject(ImagesUtil);
  readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  logo = 'assets/images/capgemini_logo_fundacion.svg';

  elements: (
    | {
      type: 'dropdown';
      placeholder: string;
      suggestions: {
        name: string;
        url: string;
        isDropdown?: boolean;
        subdropdown?: DropdownInterface[];
      }[];
      displaySuggestions: boolean;
    }
    | {
      type: 'button';
      label: string;
      url: string;
      isExternal: boolean;
    }
  )[] = [];

  isMobile = false;
  menuOpen = false;

  currentSubmenu: {
    title: string;
    items: {
      name: string;
      url: string;
    }[];
  } | null = null;



  @ViewChild('mobilePanel') mobilePanel!: ElementRef<HTMLElement>;
  @ViewChild('hamburgerBtn') hamburgerBtn!: ButtonComponent;
  @ViewChildren('menuLink') menuLinks!: QueryList<ElementRef>;

  ngOnInit(): void {
    if (this.navbar?.logo?.image?.url) {
      this.logo = this.imageUtils.getImageUrl(this.navbar.logo.image.url);
    }

    this.isMobile = false;
    this.cdr.detectChanges();

    this.isMobile = this.shouldCollapseByOverflow();
    // this.isMobile = true; // Forzar modo móvil para pruebas

    this.elements = this.navbar.mainNavigation.map(nav => {
      if (nav.isDropdown) {
        return {
          type: 'dropdown',
          placeholder: nav.name,
          suggestions: nav.dropDownLinks?.map(link => ({
            name: link.name,
            url: link.url,
            isDropdown: link.isDropdown ?? false,
            subdropdown: link.subdropdown?.map(sub => ({
              name: sub.name,
              url: sub.url,
              isDropdown: sub.isDropdown ?? false,
              subdropdown: sub.subdropdown ?? [],
            })) ?? [],
          })) ?? [],
          displaySuggestions: false,
        };
      }

      return {
        type: 'button',
        label: nav.name,
        url: nav.url ?? '',
        isExternal: nav.isExternal ?? false,
      };
    });

    setTimeout(() => {
      this.isMobile = false;
      this.cdr.detectChanges();
      this.isMobile = this.shouldCollapseByOverflow();
    }, 0);
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.isMobile) {
      this.isMobile = false;
      this.menuOpen = false;
      this.cdr.detectChanges();
    }

    const nextIsMobile = this.shouldCollapseByOverflow();

    if (this.isMobile !== nextIsMobile) {
      this.isMobile = nextIsMobile;
      if (!this.isMobile) {
        this.menuOpen = false;
      }
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeydownDoc(ev: KeyboardEvent): void {
    if (!this.isMobile || !this.menuOpen) return;

    if (ev.key === 'Escape') {
      ev.preventDefault();
      this.menuOpen = false;
      document.body.style.overflow = 'auto';
      this.hamburgerBtn?.getNativeElement()?.focus();
    }
  }

  getImageUrl(imagePath: string): void {
    this.logo = this.imageUtils.getImageUrl(imagePath);
  }

  isDropdownSelected(suggestions: { name: string; url: string }[]): boolean {
    return suggestions.some((s) => this.router.url === s.url);
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
    document.body.style.overflow = this.menuOpen ? 'hidden' : 'auto';

    if (this.menuOpen) {
      setTimeout(() => {
        this.menuLinks.first?.nativeElement.focus();
      }, 0);
    } else {
      setTimeout(() => {
        this.hamburgerBtn?.getNativeElement()?.focus();
      }, 0);
    }
  }

  handleKeyDown(event: KeyboardEvent) {
    if (!this.menuOpen) return;

    const items = this.getFocusableItems();
    const currentIndex = items.indexOf(document.activeElement as HTMLElement);

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const nextIndex = (currentIndex + 1) % items.length;
      items[nextIndex]?.focus();
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const prevIndex = (currentIndex - 1 + items.length) % items.length;
      items[prevIndex]?.focus();
    }

    if (event.key === 'Tab') {
      return;
    }

    if (event.key === 'Escape') {
      this.toggleMenu();
      this.hamburgerBtn?.getNativeElement()?.focus();
    }
  }

  private getFocusableItems(): HTMLElement[] {
    const root = this.mobilePanel?.nativeElement;
    if (!root) return [];
    return Array.from(
      root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );
  }

  toggleDropdown(index: number): void {
    this.elements = this.elements.map((el, i) => {
      if (el.type === 'dropdown') {
        return {
          ...el,
          displaySuggestions: i === index ? !el.displaySuggestions : false,
        };
      }
      return el;
    });
  }

  onHamburgerKeydown(ev: KeyboardEvent): void {
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      this.toggleMenu();
    }
  }

  onMobileDropdownSelect(url: string): void {
    this.router.navigate([url]);
    this.toggleMenu();
  }

  ngOnDestroy(): void {
    document.body.style.overflow = document.body.style.overflow = 'auto';
  }

  private shouldCollapseByOverflow(): boolean {
    const header = document.querySelector('header.container') as HTMLElement | null;
    if (!header) {
      return this.isMobile;
    }

    const available = header.clientWidth;

    const inner = header.querySelector('.inner-container') as HTMLElement | null; // logo + menú
    const right = header.querySelector('.navbar-right') as HTMLElement | null; // CTA

    if (!inner || !right) {
      return this.isMobile;
    }

    const innerStyles = window.getComputedStyle(inner);
    const ml = parseFloat(innerStyles.marginLeft || '0') || 0;
    const mr = parseFloat(innerStyles.marginRight || '0') || 0;
    const innerTotal = inner.offsetWidth + ml + mr;

    const rightTotal = right.offsetWidth;

    const buffer = 4;

    const required = innerTotal + rightTotal + buffer;

    return required > available;
  }

  openSubmenu(
    title: string,
    items: { name: string; url: string }[]
  ): void {
    this.currentSubmenu = {
      title,
      items
    };
  }

  closeSubmenu(): void {
    this.currentSubmenu = null;
  }

  openMobileSubmenu(item: DropdownInterface): void {
    this.submenuSelected.emit({
      title: item.name,
      items: item.subdropdown ?? []
    });
  }
}
